import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Group, Box, useMantineTheme } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { Avatar } from '~/domains/design-system/Avatar';
import { Title } from '~/domains/design-system/Title';

import { getContestThumbnail } from '../../utils/getContestThumbnail';
import useStyles from '~/domains/common/components/AlertCard/alertCard.styles';
import type { ContestNotification } from './types';

const AVATAR_SIZE = 48;

function Divider() {
  const { classes } = useStyles();
  return (
    <Text className={classes.metaDivider} variant="body-small">
      •
    </Text>
  );
}

function ContestNotificationCardContestInfo({
  notification,
}: {
  notification: ContestNotification;
}) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const { contest } = notification;

  const commissionerHandle = contest.commissioner?.handle || contest.commissioner?.username;

  const avatarSrc = getContestThumbnail({
    contestThumbnailUrl: contest.thumbnail_url,
    createdByAdmin: contest.created_by_admin,
    avatarUrl: contest.commissioner.avatarUrl,
  });

  const getAlertText = () => {
    if (notification.alerts.length === 0) {
      return t('common.contestNotificationCard.contestInfo.noAlerts');
    }

    if (notification.alerts.length === 1) {
      return notification.alerts[0].message;
    }

    return `${notification.alerts.length} ${t('common.contestNotificationCard.contestInfo.alerts')}`;
  };

  return (
    <Group className={classes.contestSection} spacing="sm">
      <Avatar handle={commissionerHandle} size={AVATAR_SIZE} src={avatarSrc} />
      <Box className={classes.contestInfo}>
        <Title variant="headline-medium" className={classes.contestName}>
          {contest.name}
        </Title>
        <Group className={classes.contestMeta}>
          <Text className={classes.metaLabel} truncate>
            {commissionerHandle ? `@${commissionerHandle}` : 'Unnamed'}
          </Text>
          <Divider />
          <Text variant="body-medium" color={theme.colors.red[3]} fw={600}>
            {getAlertText()}
          </Text>
        </Group>
      </Box>
    </Group>
  );
}

export default ContestNotificationCardContestInfo;
